import { Download, Eye, Loader2 } from 'lucide-react';
import { capitalizeFirstLetter, formatMoney } from '@/shared/lib/format/money';
import { Pager, StatusPill, Th } from '../WalletPagePrimitives';
import { asDisplayText, fmtDisplayDate, reasonConvertor, TransactionReason } from '../walletPage.utils';
import type { Txn } from '../WalletPage.types';

type TransactionsTableProps = {
  txns: Txn[];
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  isFetching?: boolean;
  onPageChange: (page: number) => void;
  invoiceLoadingId?: string;
  onViewDetails: (txn: Txn) => void;
  onDownloadInvoice: (txn: Txn) => void;
};

export function TransactionsTable({
  txns,
  page,
  totalPages,
  total,
  pageSize,
  isFetching = false,
  onPageChange,
  invoiceLoadingId,
  onViewDetails,
  onDownloadInvoice,
}: TransactionsTableProps) {
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px] text-left text-sm">
          <thead>
            <tr className="border-b border-[var(--dash-line)]">
              <Th>Reason</Th>
              <Th>Description</Th>
              <Th>Amount</Th>
              <Th>Status</Th>
              <Th>Type</Th>
              <Th>Date</Th>
              <Th className="text-center">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {txns.map((t) => {
              const loading = invoiceLoadingId === t._id;
              return (
                <tr
                  key={t._id}
                  className="border-b border-[var(--dash-line)] last:border-0 hover:bg-[var(--dash-soft)]/40"
                >
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1.5 text-xs font-semibold
                      `}
                    >
                      {reasonConvertor(t.reason as TransactionReason)}
                    </span>
                  </td>
                  <td className="max-w-[240px] px-4 py-3 text-[var(--dash-muted)]">
                    <p
                      className="m-0 truncate"
                      title={asDisplayText(t.description) || undefined}
                    >
                      {asDisplayText(t.description, '—')}
                    </p>
                  </td>
                  <td className="px-4 py-3 font-semibold text-[var(--dash-ink)]">
                    {formatMoney(t.amount)}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill value={t.paymentStatus} asIs />
                    {/* {t.paymentStatus} */}
                  </td>
                  <td className="px-4 py-3">
                    {capitalizeFirstLetter(t?.type as string)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-[var(--dash-muted)]">
                    {fmtDisplayDate(t.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        type="button"
                        onClick={() => onViewDetails(t)}
                        title="View transaction details"
                        aria-label="View transaction details"
                        className="rounded-md p-1.5 text-[var(--dash-muted)] hover:bg-[var(--dash-soft)] hover:text-[var(--dash-brand)]"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDownloadInvoice(t)}
                        disabled={loading}
                        title="Download invoice"
                        aria-label="Download invoice"
                        className="rounded-md p-1.5 text-[var(--dash-muted)] hover:bg-[var(--dash-soft)] hover:text-[var(--dash-brand)] disabled:opacity-50"
                      >
                        {loading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pager
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={pageSize}
        isFetching={isFetching}
        onChange={onPageChange}
      />
    </>
  );
}
